'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Modal } from '@shared/ui/modal/Modal';
import { PrimaryButton } from '@shared/ui/button/PrimaryButton';
import { Typography } from '@shared/ui/typography/Typography';
import { Icon } from '@shared/ui/icons/Icon';
import type { BaseModalProps } from '@shared/types/modal.types';
import { verifyEmail, resendVerificationEmail } from '@entities/user/api';
import { useAuthStore } from '@shared/lib/store/auth-store';
import { parseVerificationError, secondsToMinutes } from '@shared/lib/utils/error-parser';

interface ConfirmEmailModalProps extends BaseModalProps {
  email: string;
  onVerified?: () => void;
}

/**
 * Email Confirmation Modal
 * Shows after registration, waits for email verification
 * Includes rate limit handling and countdown timer
 * Usage: const { open } = useModal('confirm-email'); open({ email: 'user@example.com' });
 */
export function ConfirmEmailModal({
  isOpen,
  onClose,
  email,
  onVerified,
}: ConfirmEmailModalProps) {
  const [isVerified, setIsVerified] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [countdown, setCountdown] = useState<number>(0);
  const t = useTranslations('auth.confirmEmail');
  const tErrors = useTranslations('auth.login.errors');
  const { user, isAuthenticated, updateUser } = useAuthStore();

  // Poll for email verification status by checking auth store
  useEffect(() => {
    if (!isOpen || isVerified) return;

    // Check verification status every 3 seconds
    const intervalId = setInterval(() => {
      setIsChecking(true);
      try {
        // Check if user is authenticated and email is verified
        if (isAuthenticated && user?.isVerified) {
          setIsVerified(true);
          clearInterval(intervalId);
        }
      } catch (err) {
        console.error('Error checking verification status:', err);
      } finally {
        setIsChecking(false);
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [isOpen, isVerified, isAuthenticated, user, updateUser]);

  // Countdown timer for rate limiting
  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setRetryAfter(null);
            setError(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [countdown]);

  const handleContinue = () => {
    onVerified?.();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      animation="fade"
      showCloseButton={true}
      className="!max-w-[500px] !rounded-[30px]"
    >
      <div className="flex flex-col items-center p-8 text-center">
        {/* Icon/Illustration */}
        <div className="relative w-full h-[100px] mb-6">
          {isVerified ? (
            // Brand logo
            <div className="w-full h-full flex items-center justify-center">
              <Image
                src="/images/modal/done.svg"
                alt="Hummii"
                width={280}
                height={280}
                className="object-contain"
                priority
              />
            </div>
          ) : (
            // Email illustration
            <Image
              src="/images/modal/confirm-email.svg"
              alt="Email sent"
              fill
              className="object-contain"
              priority
            />
          )}
        </div>

        {/* Title */}
        <Typography variant="h2" className="mb-3">
          {isVerified ? t('verified.title') : t('title')}
        </Typography>

        {/* Description */}
        <Typography variant="bodySm" color="secondary" className="mb-6 max-w-[400px]">
          {isVerified ? (
            t('verified.description')
          ) : (
            <>
              {t('description')}{' '}
              <span className="text-text-primary font-semibold">{email}</span>
            </>
          )}
        </Typography>

        {/* Error message */}
        {error && (
          <div className="w-full p-3 mb-4 bg-feedback-error/10 border border-feedback-error rounded-lg">
            <Typography variant="bodySm" color="error">
              {error}
            </Typography>
          </div>
        )}

        {/* Status indicator */}
        {!isVerified && (
          <div className="flex items-center gap-2 mb-6">
            <div className="w-2 h-2 bg-accent-primary rounded-full animate-pulse" />
            <Typography variant="bodySm" color="secondary">
              {t('waiting')}
            </Typography>
          </div>
        )}

        {/* Actions */}
        <div className="w-full space-y-3">
          {isVerified ? (
            <PrimaryButton fullWidth onClick={handleContinue}>
              {t('verified.button')}
            </PrimaryButton>
          ) : (
            <>
              <Typography variant="bodySm" color="tertiary" className="mb-4">
                {t('checkSpam')}
              </Typography>

              {/* Resend email button could go here */}
            </>
          )}
        </div>

        {/* Success message */}
        {showSuccess && (
          <div className="w-full p-3 mb-4 bg-feedback-success/10 border border-feedback-success rounded-lg">
            <Typography variant="bodySm" color="success">
              {t('resendSuccess')}
            </Typography>
          </div>
        )}

        {/* Additional info */}
        {!isVerified && (
          <div className="mt-6 pt-6 border-t border-border-secondary w-full">
            <Typography variant="bodySm" color="tertiary">
              {t('didntReceive')}{' '}
              <button
                className="text-accent-primary font-semibold hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isResending || !!retryAfter}
                onClick={async () => {
                  if (retryAfter) {
                    return; // Prevent resend during rate limit
                  }

                  setIsResending(true);
                  setError(null);
                  setShowSuccess(false);

                  try {
                    await resendVerificationEmail(email);
                    setShowSuccess(true);

                    // Hide success message after 5 seconds
                    setTimeout(() => setShowSuccess(false), 5000);
                  } catch (err) {
                    const parsedError = parseVerificationError(err);

                    if (parsedError.type === 'rate_limit' && parsedError.retryAfter) {
                      const minutes = secondsToMinutes(parsedError.retryAfter);
                      setError(tErrors('resendLimitReached', { minutes }));
                      setRetryAfter(parsedError.retryAfter);
                      setCountdown(parsedError.retryAfter);
                    } else {
                      setError(t('resendError'));
                    }
                  } finally {
                    setIsResending(false);
                  }
                }}
              >
                {isResending
                  ? t('sending')
                  : retryAfter && countdown > 0
                  ? `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, '0')}`
                  : t('resend')}
              </button>
            </Typography>
          </div>
        )}
      </div>
    </Modal>
  );
}
